import React, { useState, useCallback, memo } from 'react'
import {
  Box,
  TableCell,
  TableRow,
  TextField,
  InputAdornment,
  Dialog,
  IconButton,
  Stack,
} from '@mui/material'
import dayjs from 'dayjs'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import TodayIcon from '@mui/icons-material/Today'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { Patient } from '@/features/patients/types/patients'
import PatientDetailDrawer from '@/apps/latest/components/portal/PatientDetailDrawer'
import PatientNeedCheckCell from '@/apps/latest/components/portal/PatientNeedCheckCell'
import FlexibleActionFilters from '@/apps/latest/components/portal/FlexibleActionFilters'
import { usePatientsQuery } from '@/features/patients'
import { usePatientListUrlParams } from '@/features/internal/patients-portal/hooks/usePatientListUrlParams'
import { useScrollRestoration } from '@/features/internal/patients-portal/hooks/useScrollRestoration'
import { PAGINATION_LIMITS } from '@/apps/latest/constants/patientConstants'
import { LoadingIndicator } from '@/apps/latest/components/atoms/LoadingIndicator'
import {
  Banner,
  DataTable,
  type DataTableColumn,
  FilterBar,
  NotionButton,
  NotionDatePicker,
  NotionPagination,
  NotionSelect,
  PageLoading,
  PageScaffold,
  TableToolbar,
} from '@/apps/latest/components/portal/notion'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'

interface PatientTableRowProps {
  patient: Patient
  onShowDetail: (patient: Patient) => void
}

const PatientTableRow = memo<PatientTableRowProps>(function PatientTableRow({
  patient,
  onShowDetail,
}) {
  return (
    <TableRow>
      <TableCell>{patient.name_kanji}</TableCell>
      <TableCell>{patient.name_kana}</TableCell>
      <TableCell>{patient.patient_code}</TableCell>
      <TableCell>{patient.last_visit_date}</TableCell>
      <TableCell>
        <PatientNeedCheckCell patient={patient} />
      </TableCell>
      <TableCell align="right">
        <NotionButton variant="ghost" onClick={() => onShowDetail(patient)}>
          詳細
        </NotionButton>
      </TableCell>
    </TableRow>
  )
})

type SortField = 'name_kanji' | 'name_kana' | 'patient_code' | 'last_visit_date'

const sortIcon = (field: SortField, current: string, order: 'asc' | 'desc') => {
  if (field !== current) return null
  return order === 'asc' ? (
    <ArrowUpwardIcon sx={{ fontSize: 12 }} />
  ) : (
    <ArrowDownwardIcon sx={{ fontSize: 12 }} />
  )
}

const PatientsPage: React.FC = () => {
  const {
    apiParams,
    inputValues,
    visitDate,
    actionStatus,
    actionGroup,
    sortField,
    sortOrder,
    page,
    limit,
    setNameKanji,
    setNameKana,
    setPatientCode,
    setVisitDate,
    setPage,
    setLimit,
    setSort,
    setActionStatus,
    setActionGroup,
    jumpToToday,
    clearAllFilters,
  } = usePatientListUrlParams()

  const { data, isLoading, error } = usePatientsQuery({
    params: apiParams,
  })
  const patients = data?.results ?? []
  const total = data?.total ?? 0

  const { saveScrollPosition, scrollToTop } = useScrollRestoration(
    !isLoading && patients.length > 0
  )

  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false)

  const handleShowDetail = useCallback(
    (patient: Patient) => {
      saveScrollPosition()
      setSelectedPatient(patient)
      setDetailDrawerOpen(true)
    },
    [saveScrollPosition]
  )

  const handleCloseDetail = useCallback(() => {
    setDetailDrawerOpen(false)
    setTimeout(() => setSelectedPatient(null), 300)
  }, [])

  const handlePageChange = useCallback(
    (_event: React.ChangeEvent<unknown>, newPage: number) => {
      setPage(newPage)
    },
    [setPage]
  )

  const handleLimitChange = useCallback(
    (value: number) => {
      setLimit(value)
    },
    [setLimit]
  )

  const hasActiveFilters =
    inputValues.nameKanji ||
    inputValues.nameKana ||
    inputValues.patientCode ||
    visitDate

  if (isLoading && patients.length === 0) {
    return (
      <PageScaffold title="患者管理">
        <PageLoading />
      </PageScaffold>
    )
  }
  if (error) {
    return (
      <PageScaffold title="患者管理">
        <Banner severity="danger">エラー: {error?.message}</Banner>
      </PageScaffold>
    )
  }

  const columns: DataTableColumn[] = [
    {
      key: 'name_kanji',
      label: (
        <Stack direction="row" alignItems="center" gap={0.5}>
          患者名（漢字）
          {sortIcon('name_kanji', sortField, sortOrder)}
        </Stack>
      ),
      onHeaderClick: () => setSort('name_kanji'),
    },
    {
      key: 'name_kana',
      label: (
        <Stack direction="row" alignItems="center" gap={0.5}>
          患者名（カナ）
          {sortIcon('name_kana', sortField, sortOrder)}
        </Stack>
      ),
      onHeaderClick: () => setSort('name_kana'),
    },
    {
      key: 'patient_code',
      label: (
        <Stack direction="row" alignItems="center" gap={0.5}>
          患者 ID
          {sortIcon('patient_code', sortField, sortOrder)}
        </Stack>
      ),
      onHeaderClick: () => setSort('patient_code'),
    },
    {
      key: 'last_visit_date',
      label: (
        <Stack direction="row" alignItems="center" gap={0.5}>
          前回来局日
          {sortIcon('last_visit_date', sortField, sortOrder)}
        </Stack>
      ),
      onHeaderClick: () => setSort('last_visit_date'),
    },
    { key: 'need_check', label: '確認事項' },
    { key: 'actions', label: '', align: 'right' },
  ]

  return (
    <PageScaffold title="患者管理" meta={`全 ${total.toLocaleString()} 件`}>
      <FilterBar
        trailing={
          <>
            <NotionButton
              variant="ghost"
              startIcon={<TodayIcon sx={{ fontSize: 14 }} />}
              onClick={() => {
                jumpToToday()
                scrollToTop()
              }}
            >
              今日へ
            </NotionButton>
            {hasActiveFilters && (
              <NotionButton variant="ghost" onClick={clearAllFilters}>
                クリア
              </NotionButton>
            )}
          </>
        }
      >
        <TextField
          size="small"
          placeholder="患者名（漢字）"
          value={inputValues.nameKanji}
          onChange={(e) => setNameKanji(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          size="small"
          placeholder="患者名（カナ）"
          value={inputValues.nameKana}
          onChange={(e) => setNameKana(e.target.value)}
        />
        <TextField
          size="small"
          placeholder="患者 ID"
          value={inputValues.patientCode}
          onChange={(e) => setPatientCode(e.target.value)}
        />
        <NotionDatePicker
          label="来局日"
          value={visitDate ? dayjs(visitDate) : null}
          onChange={(v) => setVisitDate(v ? v.format('YYYY-MM-DD') : '')}
        />
      </FilterBar>

      <Box>
        <FlexibleActionFilters
          selectedGroup={actionGroup}
          selectedStatus={actionStatus}
          onGroupChange={(group) => setActionGroup(group)}
          onStatusChange={(status) => setActionStatus(status)}
        />
      </Box>

      <TableToolbar
        caption={
          <Stack
            component="span"
            direction="row"
            alignItems="center"
            gap={1}
            display="inline-flex"
          >
            {`${(page - 1) * limit + 1}-${Math.min(page * limit, total)} 件`}
            {isLoading && (
              <Box sx={{ display: 'inline-flex' }}>
                <LoadingIndicator message="" size={14} />
              </Box>
            )}
          </Stack>
        }
        actions={
          <NotionSelect<number>
            label="表示件数"
            value={limit}
            onChange={handleLimitChange}
            minWidth={120}
            options={PAGINATION_LIMITS.map((opt) => ({
              value: opt,
              label: `${opt} 件`,
            }))}
          />
        }
      />

      <Box
        sx={{
          opacity: isLoading ? 0.6 : 1,
          transition: 'opacity 0.2s ease',
        }}
      >
        <DataTable columns={columns}>
          {patients.map((patient) => (
            <PatientTableRow
              key={patient.id}
              patient={patient}
              onShowDetail={handleShowDetail}
            />
          ))}
        </DataTable>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
        <NotionPagination
          count={Math.ceil(total / limit)}
          page={page}
          onChange={handlePageChange}
          showFirstButton
          showLastButton
          siblingCount={2}
          boundaryCount={1}
          size="small"
        />
      </Box>

      <Dialog
        open={detailDrawerOpen}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: `${RADIUS.md}px`,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            px: 1,
            py: 0.5,
            borderBottom: `1px solid ${colorTokens.border.subtle}`,
            flexShrink: 0,
          }}
        >
          <IconButton size="small" onClick={handleCloseDetail}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {selectedPatient && (
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <PatientDetailDrawer
              patient={selectedPatient}
              onClose={handleCloseDetail}
            />
          </Box>
        )}
      </Dialog>
    </PageScaffold>
  )
}

export default PatientsPage
